import { ApolloClient, createHttpLink, DocumentNode, gql, InMemoryCache, useMutation, useQuery } from '@apollo/client';
import { Autocomplete, Chip, createFilterOptions, ListItemText, TextField } from '@mui/material';
import { isEqual, uniqWith } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useStyling } from '../Theme';
import { UserContext } from '../auth/UserContext';
import { authLink } from '../auth/api';
import { DEFAULT_WIDGET } from '../constants/constants';
import ChipOptionsRenderer, { RendererType } from '../form/widgets/ChipOptionsRenderer';
import { searchClient, slimResultsQuery } from '../list/slimQuery';
import { arrayToSelectOptions } from '../utils/arrays';
import { AIReferenceFieldStyling } from '../utils/commonUtils';
import AIAdornment from './AIAdornment';
import { SortableChips } from './SortableChipsComponents/SortableChips';

const filter = createFilterOptions();

type Term = {
    text: {
        value: string;
        languageCode: string;
    };
    abbreviation?: string;
    definition?: string;
};

export const metadataClient = new ApolloClient({
    link: authLink.concat(
        createHttpLink({
            uri: '/metadata/v1/'
        })
    ),
    cache: new InMemoryCache({
        addTypename: false
    })
});

export const createTermMutation: DocumentNode = gql`
    mutation AutoCreateTermset($termset: String!, $input: TermInput!) {
        autoCreateTermset(termset: $termset, input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

const TILE_WIDTH = 80;
const PLACEHOLDER_MIN_WIDTH = 42;
const TAG_LIMIT_SIZE = 25;
const AUTOCOMPLETE_INPUT_PADDING = 71;
const TAG_GAP = 4;
const AUTOCOMPLETE_WIDTH_SUBTRAHEND = PLACEHOLDER_MIN_WIDTH + TAG_LIMIT_SIZE + AUTOCOMPLETE_INPUT_PADDING;

export default function MetadataAutocomplete(props) {
    const {
        id,
        metadataName: entity,
        inputLabel,
        placeholder,
        value,
        onChange,
        multiple = false,
        disabled = false,
        required = false,
        hasError = undefined,
        errorText = undefined,
        dependentFieldValue,
        allowCreate = false,
        lazyLoad = false,
        pageSize = 5000,
        limitTags = 1,
        optionValues = undefined,
        variant = DEFAULT_WIDGET.defaultWidgetVariant,
        size = DEFAULT_WIDGET.defaultWidgetSize,
        query = slimResultsQuery(entity),
        filters = [],
        identifier = 'entity',
        simpleCategory = false,
        termset = undefined,
        sortBy = 'name_ASC',
        optionsSort = true,
        testId = undefined,
        fixedOptions = [],
        isAIExtractedData = false,
        autoFocus = false,
        tooltip = undefined,
        draggableChips = false,
        ...rest
    } = props;
    const { activeOrganizationAccount } = useContext(UserContext);
    const [isAIExtractedValue, setIsAIExtractedValue] = useState(isAIExtractedData);
    const autocompleteRef = useRef(null);
    const [dynamicLimitTags, setDynamicLimitTags] = useState<number>(-1);
    const { aiExtractedFieldColor } = useStyling();

    useEffect(() => {
        if (autocompleteRef.current) {
            updateLimitTags();
        }
        window.addEventListener('resize', updateLimitTags);
        return () => {
            window.removeEventListener('resize', updateLimitTags);
        };
    }, []);

    const updateLimitTags = () => {
        //calculate the number of tags that will be shown inside the Autocomplete input
        // depending on the width of the component
        setDynamicLimitTags(
            Math.floor((autocompleteRef.current.clientWidth - AUTOCOMPLETE_WIDTH_SUBTRAHEND) / (TILE_WIDTH + TAG_GAP))
        );
    };

    const filterArray = [{ identifier, value: entity }, ...filters];

    const createOptionByEntity = (items, addSubLabel) => {
        return items.map((item) => ({
            label: item.name,
            ...(addSubLabel && { subLabel: item.lineage ? item.lineage.value : item.name })
        }));
    };

    const createCatalogSubTypeOptions = (items) => {
        return items
            .filter((item) => item.catalogTypes?.includes(dependentFieldValue))
            .map((item) => ({ label: item.name }));
    };

    const createEntityOptionsMap = () => {
        const optionsMap = new Map([
            ['Category', (items) => createOptionByEntity(items, true)],
            ['default', (items) => createOptionByEntity(items, false)]
        ]);

        if (dependentFieldValue) {
            optionsMap.set('CatalogSubType', createCatalogSubTypeOptions);
        }

        return optionsMap;
    };

    const toOptionsByEntity = createEntityOptionsMap();

    const optionEqualsValueByEntity = new Map([
        [
            'Category',
            (option, value) => {
                return option && value ? option.label === value.label && option.subLabel === value.subLabel : false;
            }
        ],
        [
            'default',
            (option, value) => {
                if (!option || !value) {
                    return false;
                }
                const optionLabel = String(option.label ?? '')
                    .trim()
                    .toLowerCase();
                const valueLabel = String(value.label ?? value)
                    .trim()
                    .toLowerCase();
                return optionLabel === valueLabel;
            }
        ]
    ]);

    const toSelectedValueByEntity = new Map([
        [
            'Category',
            (option) => {
                if (simpleCategory) {
                    return option.label;
                } else {
                    return option ? { value: option.label, lineage: { value: option.subLabel } } : null;
                }
            }
        ],
        [
            'default',
            (option) => {
                return option ? (option.inputValue ? option.inputValue : option.label) : null;
            }
        ]
    ]);

    const valueToOptionByEntity = new Map([
        [
            'Category',
            (value) => {
                if (multiple) {
                    return value
                        ? value.map((v) => {
                              if (typeof v === 'string') {
                                  return { label: v, subLabel: v };
                              } else {
                                  return { label: v.value, subLabel: v.lineage ? v.lineage.value : v.value };
                              }
                          })
                        : [];
                } else if (typeof value === 'string') {
                    return { label: value, subLabel: value };
                } else if (value) {
                    return { label: value.value, subLabel: value.lineage ? value.lineage.value : value.value };
                } else {
                    return null;
                }
            }
        ],
        [
            'default',
            (value) => {
                if (multiple) {
                    return value
                        ? value.map((v) => {
                              return { label: v };
                          })
                        : [];
                }
                return value && value !== '' ? { label: value } : null;
            }
        ]
    ]);

    const getByEntity = (map) => {
        return map.has(entity) ? map.get(entity) : map.get('default');
    };

    const [options, setOptions] = useState(optionValues ? arrayToSelectOptions(optionValues) : []);
    const [focused, setFocused] = useState(false);

    const { data, loading, refetch } = useQuery(query, {
        client: searchClient,
        skip: (lazyLoad && !focused) || optionValues,
        variables: { page: { from: 0, size: pageSize }, filters: filterArray, sortBy },
        fetchPolicy: 'cache-first',
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        notifyOnNetworkStatusChange: true
    });

    const [createTerm] = useMutation(createTermMutation, {
        client: metadataClient,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        notifyOnNetworkStatusChange: true
    });

    const sortOptions = (options) => {
        return options.sort((o1, o2) => o1.label.localeCompare(o2.label));
    };

    const loadMore = () => {
        if (data.results.hits.page.pageNumber < data.results.hits.page.totalPages - 1) {
            refetch({
                page: {
                    from: (data.results.hits.page.pageNumber + 1) * data.results.hits.page.size,
                    size: data.results.hits.page.size
                },
                filters: filterArray
            });
        }
    };

    useEffect(() => {
        if (autocompleteRef.current && autoFocus) {
            const input = autocompleteRef.current.querySelector('input');
            input?.focus();
        }
    }, [autoFocus]);

    useEffect(() => {
        if (data && data.results.hits && data.results.hits.items) {
            const items = data.results.hits.items;
            if (items.length) {
                const optionsData = uniqWith([...options, ...getByEntity(toOptionsByEntity)(items)], isEqual);
                const sortedOptions = optionsSort && sortOptions(optionsData);
                setOptions(optionsSort ? sortedOptions : optionsData);
            }
        }
    }, [data]);

    useEffect(() => {
        if (dependentFieldValue && data && data.results.hits && data.results.hits.items) {
            const items = data.results.hits.items;
            if (items.length) {
                setOptions([...getByEntity(toOptionsByEntity)(items)]);
            }
        }
    }, [dependentFieldValue, data]);

    const handleChange = (value) => {
        const toSelectedValue = getByEntity(toSelectedValueByEntity);
        if (multiple) {
            onChange(
                rest.name,
                value.map((option) => toSelectedValue(option))
            );
        } else {
            onChange(rest.name, toSelectedValue(value));
        }
    };

    const isOptionEqualToValue = (option, value) => {
        return option && value ? option.label === value : false;
    };

    const handleChipReorder = (reorderedOptions) => {
        handleChange(reorderedOptions);
    };

    if (rest.showChips) {
        return (
            <ChipOptionsRenderer
                loading={loading}
                options={options}
                value={value}
                label={inputLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                onChange={handleChange}
            />
        );
    } else if (rest.showRadio) {
        return (
            <ChipOptionsRenderer
                rendererType={RendererType.RADIO}
                loading={loading}
                options={options}
                value={value}
                label={inputLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                onChange={handleChange}
            />
        );
    } else {
        return (
            <Autocomplete
                data-testid={testId}
                ref={(ref) => (autocompleteRef.current = ref)}
                key={rest.autocompleteKey}
                size={size}
                id={id}
                multiple={multiple}
                selectOnFocus={allowCreate}
                clearOnBlur={allowCreate}
                freeSolo={allowCreate}
                fullWidth
                limitTags={dynamicLimitTags}
                loading={loading}
                options={options}
                disabled={disabled}
                getOptionLabel={(option) => {
                    return option ? (option.inputValue ? option.inputValue : option.label) : '';
                }}
                ListboxProps={{
                    onScroll: (event: React.SyntheticEvent) => {
                        const listBoxNode = event.currentTarget;
                        if (listBoxNode.scrollTop + listBoxNode.clientHeight === listBoxNode.scrollHeight) {
                            loadMore();
                        }
                    }
                }}
                renderOption={(props, option, { selected }) => {
                    return (
                        <li {...props} key={props.id}>
                            <ListItemText primary={option.label} secondary={option.subLabel} />
                        </li>
                    );
                }}
                value={getByEntity(valueToOptionByEntity)(value)}
                isOptionEqualToValue={getByEntity(optionEqualsValueByEntity)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        disabled={disabled}
                        required={required}
                        variant={variant}
                        label={inputLabel}
                        error={hasError}
                        helperText={errorText}
                        sx={AIReferenceFieldStyling(value, isAIExtractedValue, aiExtractedFieldColor)}
                        InputProps={{
                            ...params.InputProps,
                            ...(isAIExtractedValue
                                ? {
                                      startAdornment: <AIAdornment />
                                  }
                                : {}),
                            size
                        }}
                        placeholder={undefined}
                    />
                )}
                filterSelectedOptions
                filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    if (allowCreate) {
                        const { inputValue } = params;
                        // Suggest the creation of a new value
                        const isExisting = options.some((option) => inputValue === option.label);
                        if (inputValue !== '' && !isExisting) {
                            filtered.push({
                                inputValue,
                                label: `Add "${inputValue}"`
                            });
                        }
                    }

                    return filtered;
                }}
                onChange={(event, value) => {
                    if (value) {
                        (Array.isArray(value) ? value : [value]).forEach((v) => {
                            if (v.inputValue) {
                                createTerm({
                                    variables: {
                                        termset: entity || termset,
                                        input: {
                                            text: {
                                                value: v.inputValue,
                                                languageCode: 'en'
                                            }
                                        }
                                    }
                                })
                                    .then((res) => {
                                        console.log('Created term:', res);
                                        const newOptions = [
                                            ...options,
                                            {
                                                /*id: res.data.autoCreateTermset.id, */ label: v.inputValue
                                            }
                                        ];
                                        const sortedNewOptions = sortOptions(newOptions);
                                        setOptions(sortedNewOptions);
                                        refetch();
                                    })
                                    .catch((err) => {
                                        console.error('Failed to create term:', err);
                                    });
                            }
                        });
                    }
                    handleChange(value);
                    if (isAIExtractedValue) {
                        setIsAIExtractedValue(false);
                    }
                }}
                onBlur={() => setFocused(false)}
                onFocus={() => setFocused(true)}
                renderTags={
                    draggableChips
                        ? (tagValue, getTagProps) => (
                              <SortableChips
                                  chips={tagValue}
                                  getTagProps={getTagProps}
                                  fixedOptions={fixedOptions}
                                  onReorder={handleChipReorder}
                              />
                          )
                        : (tagValue, getTagProps) =>
                              tagValue.map((option, index) => (
                                  <Chip
                                      {...getTagProps({ index })}
                                      key={option.label}
                                      label={option.label}
                                      size={size}
                                  />
                              ))
                }
            />
        );
    }
}
